import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  DEFAULT_STORAGE_PREFIX,
  LocalStorageAdapter,
} from "@/infrastructure/adapters/localStorage";
import { JsonSerializer } from "@/infrastructure/adapters/serializers/JsonSerializer";
import { SidebarStatusRepository } from "@src/infrastructure/repositories/sidebar";
import { SidebarStatusUseCase } from "@src/application/useCases/sidebar";
import { SidebarContext } from "@src/presentation/hooks/sidebar/useSidebarContext";

interface SidebarProviderProps {
  children: ReactNode;
}

/**
 * Sidebar Provider - presentation layer.
 * Uses SidebarStatusUseCase and SidebarStatusRepository for open/close state.
 */
export const SidebarProvider = ({ children }: SidebarProviderProps) => {
  const sidebarRepository = useMemo(
    () =>
      new SidebarStatusRepository(
        new LocalStorageAdapter(new JsonSerializer(), DEFAULT_STORAGE_PREFIX),
        DEFAULT_STORAGE_PREFIX,
      ),
    [],
  );

  const sidebarStatusUseCase = useMemo(
    () => new SidebarStatusUseCase(sidebarRepository),
    [sidebarRepository],
  );

  const [collapsed, setCollapsed] = useState(() =>
    sidebarStatusUseCase.getCollapsedSync(),
  );

  useEffect(() => {
    sidebarStatusUseCase
      .resetCollapsed()
      .then(() => sidebarStatusUseCase.getCollapsed())
      .then(setCollapsed);
  }, [sidebarStatusUseCase]);

  const persistAndSetCollapsed = useCallback(
    (value: boolean) => {
      setCollapsed(value);
      sidebarStatusUseCase.setCollapsed(value).catch(() => {
        throw new Error("Failed to set collapsed state");
      });
    },
    [sidebarStatusUseCase],
  );

  const contextValue = useMemo(
    () => ({ collapsed, setCollapsed: persistAndSetCollapsed }),
    [collapsed, persistAndSetCollapsed],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      {children}
    </SidebarContext.Provider>
  );
};
